import React from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { getUserFullName } from "@/core/utils/name";
import { LEGEND_COLORS } from "@/dashboard/constants";
import { DetailedUserWithStatsAndJobCount } from "@/user/user";

interface ParticipationTrackerListItemProps {
  user: DetailedUserWithStatsAndJobCount;
  maxValue: number;
  showCompletedJobs: boolean;
}

export const ParticipationTrackerListItem = React.memo(
  ({
    user,
    maxValue,
    showCompletedJobs,
  }: ParticipationTrackerListItemProps) => {
    const signupCount = user.stats?.signup_count ?? 0;
    const jobsCompleted = user.job_count ?? 0;

    const signupWidthPercentage =
      maxValue > 0 ? (signupCount / maxValue) * 100 : 0;
    const jobsWidthPercentage =
      maxValue > 0 ? (jobsCompleted / maxValue) * 100 : 0;

    // Determine if signups bar should have right rounding
    const isSignupsWider = signupWidthPercentage > jobsWidthPercentage;

    return (
      <Stack
        align="start"
        gap={1}
        className="w-full border-b border-line py-3 last:border-b-0"
      >
        <Text variant="label">{getUserFullName(user)}</Text>
        <Stack
          className="w-full"
          gap={4}
          direction="row"
          align="center"
          justify="between"
        >
          <Stack
            gap={4}
            direction="row"
            align="center"
            className="relative h-2 w-full rounded-full bg-neutral"
          >
            {/* Jobs completed bar (background) - always behind */}
            <div
              className={cn("h-2 rounded-full", LEGEND_COLORS.jobs_completed)}
              style={{
                width: `${jobsWidthPercentage}%`,
              }}
            />
            {/* Signups bar (foreground) - always on top */}
            <div
              className={cn(
                "absolute h-2",
                isSignupsWider ? "rounded-full" : "rounded-l-full",
                LEGEND_COLORS.signups,
              )}
              style={{
                width: `${signupWidthPercentage}%`,
              }}
            />
          </Stack>
          <Stack
            align="start"
            direction="row"
            gap={2}
            className="ml-4 shrink-0"
          >
            <Text className="tabular-nums">{signupCount}</Text>
            {showCompletedJobs && (
              <>
                <Text tone="muted">/</Text>
                <Text className="tabular-nums">{jobsCompleted}</Text>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  },
);
